import paper from "paper";
import type { StampMeta } from "../stamps/StampMeta";
import type { StampLibrary } from "../stamps/StampLibrary";
import { normalizeStampItemToBbox } from "../stamps/stampNormalize";
import { cssVar } from "../colors";

/**
 * Standard tint applied to every bundled stamp at import time. Author SVGs
 * with whatever fill/stroke colors are convenient — the renderer overrides
 * them so all stamps read as a consistent darker-brown deboss on the leather.
 * Value comes from `--leather-decor` in CSS (same colour the Scene uses for
 * DECOR_STROKE), read lazily so a theme tweak only touches `styles.css`.
 */
const STAMP_TINT = () => cssVar("--leather-decor");
const STAMP_ALPHA = 0.85;

/**
 * Imports a stamp's SVG into a Paper.js definition that can be cloned cheaply.
 * Caches by stamp id so repeat placements don't re-parse the SVG.
 */
export class StampSymbols {
  private cache = new Map<string, paper.Item>();
  private scope: paper.PaperScope;
  private library: StampLibrary;

  constructor(scope: paper.PaperScope, library: StampLibrary) {
    this.scope = scope;
    this.library = library;
  }

  get(id: string): paper.Item | null {
    const cached = this.cache.get(id);
    if (cached) return cached;
    const meta = this.library.get(id);
    if (!meta) return null;
    const item = this.parse(meta);
    if (item) this.cache.set(id, item);
    return item;
  }

  invalidate(id?: string): void {
    if (id) this.cache.delete(id);
    else this.cache.clear();
  }

  private parse(meta: StampMeta): paper.Item | null {
    this.scope.activate();
    const wasActive = this.scope.project.activeLayer;
    const tmpLayer = new this.scope.Layer();
    tmpLayer.visible = false;
    let item: paper.Item | null = null;
    try {
      item = this.scope.project.importSVG(meta.svg, {
        expandShapes: true,
        insert: false,
      }) as paper.Item;
    } catch (e) {
      console.warn(`Failed to import stamp ${meta.id}`, e);
    }
    tmpLayer.remove();
    wasActive?.activate();
    if (!item) return null;

    // Map the imported SVG's bounds onto the declared physical bbox — the
    // same normalization the SVG exporter applies (#945), shared so the two
    // can't drift.
    normalizeStampItemToBbox(item, meta, this.scope);

    this.tint(item);
    item.applyMatrix = true;
    return item;
  }

  /** Replace any fill/stroke colors with the standard stamp tint + alpha so
   *  every bundled stamp reads consistently on leather, regardless of the
   *  colors the SVG was authored with. */
  private tint(item: paper.Item): void {
    const path = item as paper.Path;
    if ("fillColor" in path && path.fillColor) {
      const c = new this.scope.Color(STAMP_TINT());
      c.alpha = STAMP_ALPHA;
      path.fillColor = c;
    }
    if ("strokeColor" in path && path.strokeColor) {
      const c = new this.scope.Color(STAMP_TINT());
      c.alpha = STAMP_ALPHA;
      path.strokeColor = c;
    }
    if (item.children) {
      for (const child of item.children) this.tint(child);
    }
  }
}
